import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BUSINESS } from '../config/business';

const PRIMARY_CANONICAL_DOMAIN = "https://www.bpasolutions.au";

const SEO = ({ title, description, schema, canonicalPath }) => {
  const location = useLocation();

  useEffect(() => {
    if (title) {
      document.title = title.includes(BUSINESS.shortName) ? title : `${title} | ${BUSINESS.shortName}`;
    }

    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
    }

    const path = canonicalPath || location.pathname;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const fullCanonical = `${PRIMARY_CANONICAL_DOMAIN}${normalizedPath}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', fullCanonical);

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"].dynamic-schema');
    existingScripts.forEach(s => s.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach(s => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.className = 'dynamic-schema';
        script.text = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }

    const globalSchemaId = 'global-business-entity-schema';
    if (!document.getElementById(globalSchemaId)) {
      const globalScript = document.createElement('script');
      globalScript.id = globalSchemaId;
      globalScript.type = 'application/ld+json';
      globalScript.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": `${PRIMARY_CANONICAL_DOMAIN}/#organization`,
            "name": BUSINESS.name,
            "alternateName": ["BPA Solutions", "BPAS"],
            "url": PRIMARY_CANONICAL_DOMAIN,
            "logo": {
              "@type": "ImageObject",
              "url": "https://i.ibb.co/kgj60dnL/logo.webp",
              "width": "160",
              "height": "36"
            },
            "email": BUSINESS.email,
            "telephone": BUSINESS.phoneFormatted,
            "description": "BPA Solutions is a registered business based in Sydney, Australia, providing website design, web application development, business automation, CRM automation, reporting dashboards, and workflow systems for small businesses, trades, construction, service businesses, and professional services across Australia."
          },
          {
            "@type": ["ProfessionalService", "LocalBusiness"],
            "@id": `${PRIMARY_CANONICAL_DOMAIN}/#localbusiness`,
            "name": BUSINESS.name,
            "url": PRIMARY_CANONICAL_DOMAIN,
            "telephone": BUSINESS.phoneFormatted,
            "taxID": "83 679 369 932",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Sydney",
              "addressRegion": "NSW",
              "postalCode": "2000",
              "addressCountry": "AU"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": -33.8688,
              "longitude": 151.2093
            },
            "areaServed": [
              { "@type": "City", "name": "Sydney" },
              { "@type": "State", "name": "NSW" },
              { "@type": "Country", "name": "Australia" }
            ],
            "priceRange": "$$",
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "17:00"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Automation and Web Services",
              "itemListElement": [
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Website Design" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Web Application Development" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Business Automation" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "CRM Automation" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Chatbots" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Dashboard and Reporting" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "API Integrations" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Document Automation" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Workflow Automation" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "SEO and Digital Marketing" } }
              ]
            }
          }
        ]
      });
      document.head.appendChild(globalScript);
    }
  }, [title, description, schema, canonicalPath, location]);

  return null;
};

export default SEO;